import { useEffect, useMemo, useState } from "react";

import { useKBar } from "kbar";
import { useDebounce } from "use-debounce";

import { useUser } from "src/contexts/user-context";
import { useSearchWorkspacesQuery } from "src/graphql";
import { DescriptionIcon, FolderOpenIcon, UserIcon } from "@hightouch/htui";
import { toIlikePattern } from "src/utils/ilike-pattern";
import { switchWorkspace } from "src/utils/workspaces";
import {
  type RecentWorkspace,
  useRecentWorkspaces,
} from "src/hooks/use-recent-workspaces";
import { useCommandBar } from "src/components/commandbar/context";
import { useRegisterCommandActions } from "src/components/commandbar/use-register-command-actions";
import type {
  CommandAction,
  CommandWorkspaceDetails,
} from "src/components/commandbar/types";

const isNumeric = (value: string) => {
  return /^-?\d+$/.test(value);
};

const SWITCH_WORKSPACE_ROOT_ID = "workspace.switch";

const DOCS_ACTION = {
  id: "docs",
  section: "Docs",
  name: "Docs",
  keywords: "docs documentation",
  iconComponent: DescriptionIcon,
  perform: () =>
    (window.location.href = import.meta.env.VITE_DOCS_URL as string),
};

// Number of recent workspaces surfaced in the empty state of the
// "Switch workspace" command-bar menu.
const MAX_DISPLAYED_RECENT_WORKSPACES = 5;

type WorkspaceCommandDisplayInput = {
  id: string | number;
  name: string;
  slug: string | null;
  organizationName?: string | null;
  memberCount?: number | null;
  supportAccessType?: string | null;
};

/**
 * Shared formatting for "Switch workspace" command-bar entries so recents and
 * search results render identically: the workspace name, then the organization
 * (or why it can't be opened), member count and id in aligned columns.
 */
export function getWorkspaceCommandDisplay(
  workspace: WorkspaceCommandDisplayInput,
  canImpersonate: boolean,
): {
  name: string;
  subtitle: string;
  details: CommandWorkspaceDetails;
  cannotAccess: boolean;
} {
  const cannotAccess = canImpersonate && workspace.supportAccessType === "none";
  return {
    name: workspace.name,
    subtitle: cannotAccess
      ? "Support access disabled"
      : (workspace.organizationName ?? ""),
    details: {
      id: String(workspace.id),
      memberCount: workspace.memberCount ?? null,
    },
    cannotAccess,
  };
}

/**
 * Build the command-bar actions for the recently switched-to workspaces,
 * most-recent first. Excludes the active workspace and caps the result at
 * {@link MAX_DISPLAYED_RECENT_WORKSPACES}. Metadata comes straight from the
 * persisted recents, so workspaces in other organizations (e.g. customer
 * workspaces a Hightouch admin switched into) still render.
 */
export function buildRecentWorkspaceActions({
  recentWorkspaces,
  currentWorkspaceId,
  onSwitch,
}: {
  recentWorkspaces: RecentWorkspace[];
  currentWorkspaceId: unknown;
  onSwitch: (workspace: RecentWorkspace) => void;
}): CommandAction[] {
  return recentWorkspaces
    .filter((workspace) => String(workspace.id) !== String(currentWorkspaceId))
    .slice(0, MAX_DISPLAYED_RECENT_WORKSPACES)
    .map((workspace) => {
      const display = getWorkspaceCommandDisplay(workspace, false);
      return {
        id: `${SWITCH_WORKSPACE_ROOT_ID}.recent.${workspace.id}`,
        section: { name: "Recent", priority: 20 },
        parent: SWITCH_WORKSPACE_ROOT_ID,
        name: display.name,
        subtitle: display.subtitle || undefined,
        iconComponent: FolderOpenIcon,
        workspace: display.details,
        keywords: [
          workspace.id,
          workspace.name,
          workspace.slug ?? "",
          workspace.organizationName ?? "",
        ].join(" "),
        perform: () => onSwitch(workspace),
      };
    });
}

/**
 * Add dynamic Workspace options to the CommandBar
 */
export function useWorkspaceActions() {
  const { user, workspace } = useUser();
  const { setIsFetching } = useCommandBar();
  const { recentWorkspaces, addRecentWorkspace } = useRecentWorkspaces();

  // Subscribe to search query.
  const { query, search, currentRootActionId } = useKBar((state) => ({
    search: state.searchQuery,
    currentRootActionId: state.currentRootActionId,
  }));

  useRegisterCommandActions(
    [
      ...(user
        ? [
            {
              id: SWITCH_WORKSPACE_ROOT_ID,
              section: {
                name: "User",
                priority: 5,
              },
              name: "Switch workspace",
              keywords: "workspace",
              iconComponent: UserIcon,
              shortcut: ["s", "w"],
              perform: () => {
                query.setCurrentRootAction(SWITCH_WORKSPACE_ROOT_ID);
                query.toggle();
              },
            },
          ]
        : []),
      DOCS_ACTION,
    ],
    [user, query],
  );

  // Surface recently switched-to workspaces in the empty state of the
  // "Switch workspace" menu. Once the user starts typing we hand off to the
  // search results below, so recents are not shown alongside a query.
  const recentWorkspaceActions = useMemo<CommandAction[]>(() => {
    if (
      !user ||
      currentRootActionId !== SWITCH_WORKSPACE_ROOT_ID ||
      search.length > 0
    ) {
      return [];
    }
    return buildRecentWorkspaceActions({
      recentWorkspaces,
      currentWorkspaceId: workspace?.id,
      onSwitch: (recent) => {
        addRecentWorkspace(recent);
        void switchWorkspace(recent.id);
      },
    });
  }, [
    user,
    currentRootActionId,
    search,
    recentWorkspaces,
    workspace?.id,
    addRecentWorkspace,
  ]);

  const [_searchTerm, setSearchTerm] = useState("");

  const [searchTerm, { isPending: isSearchPending }] = useDebounce(
    _searchTerm,
    500,
  );

  useEffect(() => {
    if (currentRootActionId === "workspace.switch") {
      setSearchTerm(search);
    }
  }, [search, currentRootActionId]);

  const { data: workspaceData, isLoading } = useSearchWorkspacesQuery(
    {
      query: toIlikePattern(searchTerm),
      limit: 20,
      id: searchTerm.trim(),
      isIdSearch: isNumeric(searchTerm.trim()),
    },
    {
      meta: { isAdmin: user?.can_impersonate },
      enabled:
        Boolean(user) &&
        currentRootActionId === "workspace.switch" &&
        searchTerm.trim().length > 0,
      select: (data) => [
        // prioritize searches by ID since they are more specific.
        ...(data.workspacesById || []),
        ...(data.workspaces || []),
      ],
    },
  );

  const isFetching = isLoading || isSearchPending();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => setIsFetching(isFetching), [isFetching]);

  // Set to empty list if we are still loading or debouncing,
  // otherwise the command bar will try to do some in-memory filtering,
  // which looks weird as the query is loading.
  const searchedWorkspaces = isFetching ? [] : (workspaceData ?? []);

  const searchedWorkspaceActions = useMemo<CommandAction[]>(
    () =>
      user
        ? searchedWorkspaces.map((w): CommandAction => {
            const organizationName = w.organization?.name ?? null;
            const memberCount =
              w.all_memberships_aggregate.aggregate?.count ?? 0;
            // XXX: This doesn't address the case where users are directly in a workspace
            const display = getWorkspaceCommandDisplay(
              {
                id: w.id,
                name: w.name,
                slug: w.slug,
                organizationName,
                memberCount,
                supportAccessType: w.organization?.support_access_type ?? null,
              },
              Boolean(user.can_impersonate),
            );
            return {
              id: `${SWITCH_WORKSPACE_ROOT_ID}.${w.id}`,
              section: "Workspace",
              parent: SWITCH_WORKSPACE_ROOT_ID,
              name: display.name,
              subtitle: display.subtitle,
              iconComponent: FolderOpenIcon,
              workspace: display.details,
              keywords: [w.id, w.name, w.slug].join(" "),
              perform: () => {
                if (display.cannotAccess) {
                  return;
                }
                addRecentWorkspace({
                  id: String(w.id),
                  name: w.name,
                  slug: w.slug,
                  organizationName,
                  memberCount,
                });
                void switchWorkspace(w.id);
              },
            };
          })
        : [],
    [user, searchedWorkspaces, addRecentWorkspace],
  );

  useRegisterCommandActions(
    [...recentWorkspaceActions, ...searchedWorkspaceActions],
    [recentWorkspaceActions, searchedWorkspaceActions],
  );
}
